import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import PageToolbar from 'ui/PageToolbar/PageToolbar';
import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button, Input, Modal, Space, Table } from 'antd';
// Api
import { GET_BRAND_PRODUCT_CATEGORIES } from 'api/brandProductCategory/queries';
// Types
import {
  GetBrandProductCategories,
  GetBrandProductCategories_getBrandProductCategories,
} from 'api/brandProductCategory/types/GetBrandProductCategories';
// Components
import CreateEditBrandProductCategory from './components/CreateEditBrandProductCategory/CreateEditBrandProductCategory';
import DeleteBrandProductCategoryModal from './components/DeleteBrandProductCategoryModal/DeleteBrandProductCategoryModal';
// Styles
import styles from './ManageBrandProductCategories.module.scss';

type BrandProductCategory = GetBrandProductCategories_getBrandProductCategories;

const ManageBrandProductCategories = (): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedRecord, setSelectedRecord] =
    useState<BrandProductCategory | null>(null);
  const [recordToDelete, setRecordToDelete] =
    useState<BrandProductCategory | null>(null);
  const [isCreateModalVisible, setIsCreateModalVisible] =
    useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);

  const { data, loading } = useQuery<GetBrandProductCategories>(
    GET_BRAND_PRODUCT_CATEGORIES,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const brandProductCategories = data?.getBrandProductCategories;
  const categories = useMemo(
    () => brandProductCategories || [],
    [brandProductCategories]
  );

  const filteredCategories = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return categories;
    }

    return categories.filter((category) =>
      category.name.toLowerCase().includes(term)
    );
  }, [categories, searchTerm]);

  const openCreateModal = () => {
    setSelectedRecord(null);
    setIsCreateModalVisible(true);
  };

  const openEditModal = (record: BrandProductCategory) => {
    setSelectedRecord(record);
    setIsEditModalVisible(true);
  };

  const hideCreateEditModal = () => {
    setIsCreateModalVisible(false);
    setIsEditModalVisible(false);
    setSelectedRecord(null);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 320,
      align: 'center' as const,
      onCell: () => ({ style: { whiteSpace: 'nowrap' as const } }),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: BrandProductCategory, b: BrandProductCategory) =>
        a.name.localeCompare(b.name),
    },
    {
      title: 'Used by brands',
      dataIndex: 'usedByBrandsCount',
      key: 'usedByBrandsCount',
      width: 150,
      align: 'center' as const,
      sorter: (a: BrandProductCategory, b: BrandProductCategory) =>
        a.usedByBrandsCount - b.usedByBrandsCount,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      align: 'center' as const,
      width: 200,
      render: function Actions(_: unknown, record: BrandProductCategory) {
        return (
          <Space size="middle">
            <Button type="primary" onClick={() => openEditModal(record)}>
              Edit
            </Button>
            <Button danger onClick={() => setRecordToDelete(record)}>
              Delete
            </Button>
          </Space>
        );
      },
    },
  ];

  const isEditing = !!selectedRecord && isEditModalVisible;
  const isModalVisible = isCreateModalVisible || isEditing;

  return (
    <div className={styles.root}>
      <PageToolbar
        leading={
          <Input.Search
            placeholder="Search by name"
            allowClear
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
      >
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={openCreateModal}
        >
          Add product category
        </Button>
      </PageToolbar>

      <Table<BrandProductCategory>
        rowKey="id"
        columns={columns}
        dataSource={filteredCategories}
        loading={loading}
        pagination={false}
        scroll={{ x: 900 }}
      />

      <Modal
        title={isEditing ? 'Edit product category' : 'New product category'}
        visible={isModalVisible}
        onCancel={hideCreateEditModal}
        footer={null}
        destroyOnClose
      >
        <CreateEditBrandProductCategory
          record={selectedRecord}
          onFinish={hideCreateEditModal}
        />
      </Modal>

      <DeleteBrandProductCategoryModal
        record={recordToDelete}
        categories={categories}
        onClose={() => setRecordToDelete(null)}
      />
    </div>
  );
};

export default ManageBrandProductCategories;
